import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Fab,
  Grid,
  IconButton,
  MenuItem,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { DataGrid } from "@mui/x-data-grid";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import { useLocation, useNavigate } from "react-router-dom";
import { formatDate } from "../utils/dateFormat";

import PageBanner from "../components/PageBanner";
import PageContent from "../components/PageContent";
import { AppConfirmDialog, AppToast } from "../components/AppFeedback";
import DatagridNavLink from "../components/DatagridNavLink";
import { fetchClientDropdown } from "../services/clientService";
import {
  addProjectTimelinesBulk,
  addProjectUser,
  createProject,
  deleteProject,
  fetchProjectById,
  fetchProjectDatagridRows,
  fetchProjectTimelines,
  generateProjectPid,
} from "../services/projectService";
import { fetchKeycloakUsersDropdown } from "../services/keycloakUserService";
import { useUserContext } from "../context/UserContext";

const statusColorMap = {
  active: "success",
  completed: "info",
  pending: "warning",
  submitted: "default",
};

const createSteps = ["Create Project", "Add Schedules"];
const addProjectFieldSx = {
  width: "100%",
};

function toTitleCase(value = "") {
  if (!value) return "";
  return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
}

function addDaysToIsoDate(isoDate, days) {
  if (!isoDate) return "";
  const dt = new Date(isoDate);
  if (Number.isNaN(dt.getTime())) return "";
  dt.setDate(dt.getDate() + Number(days || 0));
  return dt.toISOString().slice(0, 10);
}

function toErrorMessage(error, fallbackMessage) {
  const raw = error?.message || "";

  const stringifyDetail = (detail) => {
    if (!detail) return "";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail)) {
      return detail
        .map((item) => {
          if (typeof item === "string") return item;
          if (item && typeof item === "object") {
            const loc = Array.isArray(item.loc) ? item.loc.join(".") : "";
            const msg = item.msg || JSON.stringify(item);
            return loc ? `${loc}: ${msg}` : msg;
          }
          return String(item);
        })
        .join("; ");
    }
    if (typeof detail === "object") {
      return detail.msg || JSON.stringify(detail);
    }
    return String(detail);
  };

  try {
    const parsed = JSON.parse(raw);
    if (parsed?.detail) {
      const resolved = stringifyDetail(parsed.detail);
      if (resolved) return resolved;
    }
  } catch {
    // ignore
  }
  if (typeof raw === "object") {
    const resolved = stringifyDetail(raw);
    if (resolved) return resolved;
  }
  return String(raw || fallbackMessage);
}

const initialCreateForm = {
  client: null,
  status: "active",
  name: "",
  projectType: "internal",
  people: [],
  startDate: "",
  endDate: "",
  numberOfSchedules: 1,
  pid: "",
};

const ProjectList = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { roles = [] } = useUserContext();
  const statusFilter = useMemo(() => {
    const value = new URLSearchParams(location.search).get("status") || "";
    return value.trim().toLowerCase();
  }, [location.search]);

  const [rows, setRows] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
  const [sortModel, setSortModel] = useState([{ field: "project_id", sort: "desc" }]);

  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  const [clientOptions, setClientOptions] = useState([]);
  const [selectedClients, setSelectedClients] = useState([]);

  const [deleteId, setDeleteId] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [refreshTick, setRefreshTick] = useState(0);

  const [createOpen, setCreateOpen] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [creating, setCreating] = useState(false);
  const [addingTimeline, setAddingTimeline] = useState(false);
  const [abortingCreate, setAbortingCreate] = useState(false);
  const [generatedPid, setGeneratedPid] = useState("");
  const [generatingPid, setGeneratingPid] = useState(false);
  const [keycloakUsers, setKeycloakUsers] = useState([]);
  const [loadingKeycloakUsers, setLoadingKeycloakUsers] = useState(false);
  const [createdProject, setCreatedProject] = useState(null);
  const [createForm, setCreateForm] = useState(initialCreateForm);
  const [scheduleRows, setScheduleRows] = useState([]);

  const isAdmin = roles.some((role) => String(role).toUpperCase() === "ADMIN");
  const canCreateProject = roles.some((role) => {
    const upper = String(role).toUpperCase();
    return upper === "ADMIN" || upper === "MANAGER";
  });
  const canRelaxTimelineDateBounds = canCreateProject;

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchTerm(searchInput.trim());
      setPaginationModel((prev) => ({ ...prev, page: 0 }));
    }, 400);

    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    const timer = setTimeout(async () => {
      if (!createOpen) return;
      const name = createForm.name.trim();
      const cid = createForm.client?.cid || "";
      if (!name || !cid) {
        setGeneratedPid("");
        setCreateForm((prev) => ({ ...prev, pid: "" }));
        return;
      }

      setGeneratingPid(true);
      try {
        const data = await generateProjectPid(name, cid);
        const pid = data?.pid || "";
        setGeneratedPid(pid);
        setCreateForm((prev) => ({ ...prev, pid }));
      } catch {
        setGeneratedPid("");
        setCreateForm((prev) => ({ ...prev, pid: "" }));
      } finally {
        setGeneratingPid(false);
      }
    }, 350);

    return () => clearTimeout(timer);
  }, [createForm.name, createForm.client?.cid, createOpen]);

  useEffect(() => {
    let cancelled = false;
    const loadClients = async () => {
      try {
        const clients = await fetchClientDropdown();
        if (cancelled) return;
        setClientOptions(clients || []);
      } catch {
        if (!cancelled) setClientOptions([]);
      }
    };
    loadClients();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!createOpen) return;
    let cancelled = false;

    const loadUsers = async () => {
      setLoadingKeycloakUsers(true);
      try {
        const users = await fetchKeycloakUsersDropdown({ limit: 500 });
        if (cancelled) return;
        setKeycloakUsers(users || []);
      } catch {
        if (!cancelled) setKeycloakUsers([]);
      } finally {
        if (!cancelled) setLoadingKeycloakUsers(false);
      }
    };

    loadUsers();
    return () => {
      cancelled = true;
    };
  }, [createOpen]);

  useEffect(() => {
    let cancelled = false;

    const loadData = async () => {
      setLoading(true);
      setError("");
      try {
        const sort = sortModel[0] || {};
        const response = await fetchProjectDatagridRows({
          page: paginationModel.page,
          pageSize: paginationModel.pageSize,
          search: searchTerm,
          status: statusFilter,
          clientNames: selectedClients.map((item) => item.name),
          sortField: sort.field || "project_id",
          sortOrder: sort.sort || "desc",
        });

        if (cancelled) return;
        setRows(response.rows || []);
        setRowCount(response.rowCount || 0);
      } catch (err) {
        if (!cancelled) {
          setError(err.message || "Failed to load projects");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadData();
    return () => {
      cancelled = true;
    };
  }, [paginationModel, sortModel, searchTerm, statusFilter, selectedClients, refreshTick]);

  const resetCreateModal = () => {
    setCreateOpen(false);
    setActiveStep(0);
    setCreateForm(initialCreateForm);
    setScheduleRows([]);
    setCreatedProject(null);
    setGeneratingPid(false);
    setGeneratedPid("");
  };

  const handleAbortCreateFlow = async () => {
    if (!createdProject?.id) {
      resetCreateModal();
      return;
    }

    setAbortingCreate(true);
    try {
      await deleteProject(createdProject.id);
      setSnackbar({
        open: true,
        message: "In-progress project deleted",
        severity: "info",
      });
      resetCreateModal();
      setRefreshTick((prev) => prev + 1);
    } catch (err) {
      setSnackbar({
        open: true,
        message: toErrorMessage(err, "Failed to rollback in-progress project"),
        severity: "error",
      });
    } finally {
      setAbortingCreate(false);
    }
  };

  const handleBackToCreateStep = async () => {
    // Return to project details with entries retained; roll back draft project if already created.
    if (!createdProject?.id) {
      setActiveStep(0);
      setScheduleRows([]);
      return;
    }

    setAbortingCreate(true);
    try {
      await deleteProject(createdProject.id);
      setCreatedProject(null);
      setScheduleRows([]);
      setActiveStep(0);
      setRefreshTick((prev) => prev + 1);
      setSnackbar({
        open: true,
        message: "Returned to project details. Your entries were kept.",
        severity: "info",
      });
    } catch (err) {
      setSnackbar({
        open: true,
        message: toErrorMessage(err, "Failed to go back — draft project could not be removed"),
        severity: "error",
      });
    } finally {
      setAbortingCreate(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await deleteProject(deleteId);
      setSnackbar({ open: true, message: "Project deleted successfully", severity: "success" });
      setDeleteId(null);
      setRefreshTick((prev) => prev + 1);
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.message || "Failed to delete project",
        severity: "error",
      });
      setDeleteId(null);
    }
  };

  const buildScheduleRows = (projectDetails, timelines) => {
    const totalSchedules = Number(projectDetails?.number_of_visits || 1);
    const sorted = [...(timelines || [])].sort((a, b) => Number(a.position || 0) - Number(b.position || 0));

    const mapped = [];
    for (let position = 1; position <= totalSchedules; position += 1) {
      const existing = sorted.find((item) => Number(item.position) === position);
      if (existing) {
        mapped.push({
          position,
          type: String(existing.type || "static").toLowerCase(),
          date: existing.date || "",
          tolerance: Number(existing.tolerance || 0),
          offset:
            String(existing.type || "static").toLowerCase() === "dynamic"
              ? Number(existing.offset || 1)
              : 0,
          locked: true,
        });
      } else {
        mapped.push({
          position,
          type: "static",
          date: "",
          tolerance: 0,
          offset: 0,
          locked: false,
        });
      }
    }
    return mapped;
  };

  const loadProjectForStep2 = async (projectId) => {
    const [projectDetails, timelines] = await Promise.all([
      fetchProjectById(projectId),
      fetchProjectTimelines(projectId),
    ]);

    const projectState = {
      id: projectDetails.id,
      pid: projectDetails.pid,
      startDate: projectDetails.start_date,
      endDate: projectDetails.end_date,
      numberOfSchedules: Number(projectDetails.number_of_visits || 1),
    };

    setCreatedProject(projectState);
    setCreateForm((prev) => ({
      ...prev,
      startDate: projectDetails.start_date || prev.startDate,
      endDate: projectDetails.end_date || prev.endDate,
      numberOfSchedules: Number(projectDetails.number_of_visits || prev.numberOfSchedules || 1),
    }));
    setScheduleRows(buildScheduleRows(projectDetails, timelines));
  };

  const handleCreateProjectStep = async () => {
    if (!createForm.client?.cid) {
      setSnackbar({ open: true, message: "Client name is required", severity: "warning" });
      return;
    }
    if (!createForm.name.trim()) {
      setSnackbar({ open: true, message: "Project name is required", severity: "warning" });
      return;
    }
    if (!createForm.startDate || !createForm.endDate) {
      setSnackbar({ open: true, message: "Start date and end date are required", severity: "warning" });
      return;
    }
    const scheduleCount = Number(createForm.numberOfSchedules);
    if (!Number.isInteger(scheduleCount) || scheduleCount < 1 || scheduleCount > 20) {
      setSnackbar({ open: true, message: "Number of schedules must be between 1 and 20", severity: "warning" });
      return;
    }

    const selectedUsernames = (createForm.people || []).map((item) => item.username).filter(Boolean);
    const primaryUsername = selectedUsernames.length > 0 ? selectedUsernames[0] : undefined;

    const payload = {
      cid: createForm.client.cid,
      pid: createForm.pid || undefined,
      name: createForm.name.trim(),
      description: "",
      type: createForm.projectType,
      status: createForm.status === "other" ? "pending" : createForm.status,
      start_date: createForm.startDate,
      end_date: createForm.endDate,
      number_of_visits: scheduleCount,
      username: primaryUsername,
    };

    setCreating(true);
    try {
      const created = await createProject(payload);
      const createdData = {
        id: created.id,
        pid: created.pid || createForm.pid,
      };
      setCreatedProject(createdData);

      const additionalUsernames = selectedUsernames.filter((username) => username !== primaryUsername);
      if (additionalUsernames.length > 0 && createdData.pid) {
        const results = await Promise.allSettled(
          additionalUsernames.map((username) =>
            addProjectUser({
              pid: createdData.pid,
              username,
            })
          )
        );

        const failedCount = results.filter((result) => result.status === "rejected").length;
        if (failedCount > 0) {
          setSnackbar({
            open: true,
            message: `Project created. ${failedCount} project person assignment(s) failed.`,
            severity: "warning",
          });
        }
      }

      await loadProjectForStep2(created.id);
      setSnackbar({ open: true, message: "Project created. Continue to add schedules.", severity: "success" });
      setActiveStep(1);
    } catch (err) {
      setSnackbar({
        open: true,
        message: toErrorMessage(err, "Failed to create project"),
        severity: "error",
      });
    } finally {
      setCreating(false);
    }
  };

  const getDateBoundsForPosition = (position) => {
    const startDate = createdProject?.startDate || createForm.startDate || "";
    const endDate = createdProject?.endDate || createForm.endDate || "";
    const rows = [...(scheduleRows || [])].sort((a, b) => a.position - b.position);

    let previousStaticDate = "";
    let dynamicOffsetSincePreviousStatic = 0;
    let nextStaticDate = "";

    rows.forEach((row) => {
      if (row.position < position) {
        if (row.type === "static" && row.date) {
          previousStaticDate = row.date;
          dynamicOffsetSincePreviousStatic = 0;
        } else if (row.type === "dynamic") {
          const dynamicOffset = Number(row.offset || 0);
          dynamicOffsetSincePreviousStatic += Number.isFinite(dynamicOffset) ? Math.max(dynamicOffset, 0) : 0;
        }
      } else if (!nextStaticDate && row.position > position && row.type === "static" && row.date) {
        nextStaticDate = row.date;
      }
    });

    let minDate = startDate;
    if (previousStaticDate) {
      const offsetMinDate = addDaysToIsoDate(previousStaticDate, dynamicOffsetSincePreviousStatic);
      if (!minDate || (offsetMinDate && offsetMinDate > minDate)) {
        minDate = offsetMinDate;
      }
    }

    return { minDate, maxDate: nextStaticDate || endDate };
  };

  const handleScheduleChange = (position, field, value) => {
    setScheduleRows((prev) =>
      prev.map((row) => {
        if (row.position !== position || row.locked) return row;
        if (field === "type") {
          if (position === 1) {
            return { ...row, type: "static", tolerance: 0, offset: 0 };
          }
          if (value === "static") {
            return { ...row, type: "static", tolerance: 0, offset: 0 };
          }
          return {
            ...row,
            type: "dynamic",
            date: "",
            tolerance: Number(row.tolerance) >= 0 ? Number(row.tolerance) : 0,
            offset: Number(row.offset) >= 1 ? Number(row.offset) : 1,
          };
        }
        return { ...row, [field]: value };
      })
    );
  };

  const handleAddSchedulesStep = async () => {
    if (!createdProject?.id) {
      setSnackbar({ open: true, message: "Create project first", severity: "warning" });
      return;
    }

    const newRows = [...scheduleRows]
      .filter((row) => !row.locked)
      .sort((a, b) => Number(a.position) - Number(b.position));

    if (newRows.length === 0) {
      setSnackbar({ open: true, message: "All schedules are already created", severity: "success" });
      resetCreateModal();
      setRefreshTick((prev) => prev + 1);
      return;
    }

    for (const row of newRows) {
      if (row.position === 1 && row.type !== "static") {
        setSnackbar({
          open: true,
          message: "Schedule 1 must be static",
          severity: "warning",
        });
        return;
      }
      if (row.type === "static") {
        if (row.date && !canRelaxTimelineDateBounds) {
          const { minDate, maxDate } = getDateBoundsForPosition(row.position);
          if (minDate && row.date < minDate) {
            setSnackbar({
              open: true,
              message: `Schedule ${row.position}: date must be on or after ${minDate}`,
              severity: "warning",
            });
            return;
          }
          if (maxDate && row.date > maxDate) {
            setSnackbar({
              open: true,
              message: `Schedule ${row.position}: date cannot be after ${maxDate}`,
              severity: "warning",
            });
            return;
          }
        }
      } else {
        const toleranceNumber = Number(row.tolerance);
        const offsetNumber = Number(row.offset);
        if (!Number.isInteger(toleranceNumber) || toleranceNumber < 0) {
          setSnackbar({
            open: true,
            message: `Schedule ${row.position}: dynamic tolerance must be at least 0`,
            severity: "warning",
          });
          return;
        }
        if (!Number.isInteger(offsetNumber) || offsetNumber < 1) {
          setSnackbar({
            open: true,
            message: `Schedule ${row.position}: dynamic offset must be at least 1`,
            severity: "warning",
          });
          return;
        }
      }
    }

    setAddingTimeline(true);
    try {
      const payload = {
        timelines: newRows.map((row) =>
          row.type === "static"
            ? { type: "static", tolerance: 0, offset: 0, date: row.date || null }
            : { type: "dynamic", tolerance: Number(row.tolerance), offset: Number(row.offset), date: null }
        ),
      };
      await addProjectTimelinesBulk(createdProject.id, payload);

      setSnackbar({ open: true, message: "Project schedules created successfully", severity: "success" });
      resetCreateModal();
      setRefreshTick((prev) => prev + 1);
    } catch (err) {
      setSnackbar({
        open: true,
        message: toErrorMessage(err, "Failed to add schedules"),
        severity: "error",
      });
    } finally {
      setAddingTimeline(false);
    }
  };

  const columns = useMemo(
    () => [
      {
        field: "project_id",
        headerName: "Project ID",
        flex: 1.1,
        minWidth: 140,
        renderCell: (params) => (
          <DatagridNavLink to={params.row.id ? `/project-details/${params.row.id}` : ""}>
            {params.value}
          </DatagridNavLink>
        ),
      },
      {
        field: "client_name",
        headerName: "Client Name",
        flex: 1.3,
        minWidth: 170,
        renderCell: (params) => (
          <DatagridNavLink
            to={params.row.client_id ? `/client-management/edit/${params.row.client_id}` : ""}
          >
            {params.value}
          </DatagridNavLink>
        ),
      },
      {
        field: "project_name",
        headerName: "Project Name",
        flex: 1.4,
        minWidth: 180,
        renderCell: (params) => (
          <DatagridNavLink to={params.row.id ? `/project-details/${params.row.id}` : ""}>
            {params.value}
          </DatagridNavLink>
        ),
      },
      {
        field: "project_start_date",
        headerName: "Project Start Date",
        flex: 1,
        minWidth: 150,
        renderCell: (params) => formatDate(params.value),
      },
      {
        field: "project_end_date",
        headerName: "Project End Date",
        flex: 1,
        minWidth: 150,
        renderCell: (params) => formatDate(params.value),
      },
      {
        field: "number_of_schedule",
        headerName: "Number of Schedule",
        type: "number",
        flex: 1,
        minWidth: 150,
        renderCell: (params) => (
          <DatagridNavLink to={params.row.id ? `/project-details/${params.row.id}` : ""}>
            {params.value}
          </DatagridNavLink>
        ),
      },
      {
        field: "number_of_patient",
        headerName: "Number of Subjects",
        type: "number",
        flex: 1,
        minWidth: 140,
        renderCell: (params) => (
          <DatagridNavLink to={params.row.id ? `/project-details/${params.row.id}` : ""}>
            {params.value}
          </DatagridNavLink>
        ),
      },
      {
        field: "status",
        headerName: "Status",
        flex: 1,
        minWidth: 130,
        renderCell: (params) => {
          const row = params.row || {};
          const isConfigured = Boolean(row.is_configured);
          const status = String(params.value || "").toLowerCase();
          const displayLabel = isConfigured ? toTitleCase(status) : "Not Configured";
          const chipColor = isConfigured ? (statusColorMap[status] || "default") : "warning";
          return (
            <Chip
              label={displayLabel}
              color={chipColor}
              size="small"
              variant="filled"
            />
          );
        },
      },
      {
        field: "created_at",
        headerName: "Created At",
        flex: 1,
        minWidth: 150,
        renderCell: (params) => formatDate(params.value),
      },
      {
        field: "actions",
        headerName: "Actions",
        minWidth: 130,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Tooltip title="Edit">
              <span>
                <IconButton
                  size="small"
                  color="primary"
                  onClick={(event) => {
                    event.stopPropagation();
                    navigate(`/project-details/${params.row.id}`);
                  }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Delete">
              <span>
                <IconButton
                  size="small"
                  color="error"
                  disabled={!isAdmin}
                  onClick={(event) => {
                    event.stopPropagation();
                    if (!isAdmin) return;
                    setDeleteId(params.row.id);
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        ),
      },
    ],
    [isAdmin, navigate]
  );

  return (
    <Box sx={{ bgcolor: "#dde7ecff", minHeight: "100vh" }}>
      <PageBanner title="Project" />

      <PageContent width={{ xs: "100%", sm: "95%", md: "90%" }}>
        <Box sx={{ borderRadius: 1, p: { xs: 1.5, sm: 2 }, bgcolor: "#ffffff" }}>
          <Box sx={{ mb: 2, display: "flex", gap: 2, flexWrap: "wrap", justifyContent: "flex-end" }}>
            <Autocomplete
              multiple
              size="small"
              options={clientOptions}
              value={selectedClients}
              getOptionLabel={(option) => option?.name || ""}
              onChange={(_, value) => {
                setSelectedClients(value || []);
                setPaginationModel((prev) => ({ ...prev, page: 0 }));
              }}
              isOptionEqualToValue={(option, value) => option.cid === value.cid}
              renderInput={(params) => <TextField {...params} label="Filter by Client" />}
              sx={{ width: { xs: "100%", sm: 360 } }}
            />
            <TextField
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
              placeholder="Search by project name"
              size="small"
              sx={{ width: { xs: "100%", sm: 320 } }}
            />
          </Box>

          {error ? <Alert severity="error">{error}</Alert> : null}

          <DataGrid
            rows={rows}
            columns={columns}
            loading={loading}
            pagination
            paginationMode="server"
            sortingMode="server"
            rowCount={rowCount}
            pageSizeOptions={[10, 20, 50]}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            sortModel={sortModel}
            onSortModelChange={setSortModel}
            disableRowSelectionOnClick
            getRowClassName={(params) =>
              params.indexRelativeToCurrentPage % 2 === 0 ? "row-even" : "row-odd"
            }
            autoHeight
            sx={{
              "& .row-even": {
                backgroundColor: "#ffffff",
              },
              "& .row-odd": {
                backgroundColor: "#f5f5f5",
              },
            }}
          />
        </Box>
      </PageContent>

      <AppConfirmDialog
        open={Boolean(deleteId)}
        title="Confirm Deletion"
        content={
          <>
            Are you sure you want to delete project <strong>{deleteId}</strong>?
          </>
        }
        confirmLabel="Delete"
        confirmColor="error"
        onCancel={() => setDeleteId(null)}
        onConfirm={handleDelete}
      />

      <Dialog
        open={createOpen}
        onClose={(_, reason) => {
          if (reason === "backdropClick") return;
          handleAbortCreateFlow();
        }}
        maxWidth={false}
        fullWidth
        PaperProps={{
          sx: {
            width: { xs: "96vw", sm: "90vw", md: "1100px" },
            maxWidth: "1100px",
          },
        }}
      >
        <DialogTitle sx={{ pr: 6 }}>
          Add Project
          <IconButton
            aria-label="close"
            onClick={handleAbortCreateFlow}
            disabled={creating || addingTimeline || abortingCreate}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Stepper
            activeStep={activeStep}
            sx={{
              mt: 1,
              mb: 3,
              width: { xs: "100%", sm: 360 },
              mx: "auto",
            }}
          >
            {createSteps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {activeStep === 0 ? (
            <Box sx={{ width: "100%" }}>
              <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700 }}>
                Basic Project Info
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sx={{ display: "flex", flexGrow: 1 }}>
                  <TextField
                    fullWidth
                    sx={{ ...addProjectFieldSx, flexGrow: 1 }}
                    label="Project ID (from backend)"
                    value={generatedPid}
                    disabled
                    InputProps={{ readOnly: true }}
                    helperText={generatingPid ? "Generating project ID..." : "Auto-generated by backend"}
                  />
                </Grid>
                <Grid item xs={12} sx={{ display: "flex", flexGrow: 1 }}>
                  <TextField
                    fullWidth
                    sx={{ ...addProjectFieldSx, flexGrow: 1 }}
                    required
                    label="Project Name"
                    value={createForm.name}
                    onChange={(event) => setCreateForm((prev) => ({ ...prev, name: event.target.value }))}
                  />
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700 }}>
                Assignment
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sx={{ display: "flex", flexGrow: 1 }}>
                  <Autocomplete
                    fullWidth
                    sx={{ ...addProjectFieldSx, flexGrow: 1 }}
                    options={clientOptions}
                    value={createForm.client}
                    getOptionLabel={(option) => option?.name || ""}
                    onChange={(_, value) => setCreateForm((prev) => ({ ...prev, client: value }))}
                    isOptionEqualToValue={(option, value) => option.cid === value.cid}
                    renderInput={(params) => <TextField {...params} fullWidth label="Client Name" required />}
                  />
                </Grid>
                <Grid item xs={12} sx={{ display: "flex", flexGrow: 1 }}>
                  <Autocomplete
                    fullWidth
                    sx={{ ...addProjectFieldSx, flexGrow: 1 }}
                    multiple
                    options={keycloakUsers}
                    loading={loadingKeycloakUsers}
                    value={createForm.people}
                    getOptionLabel={(option) => option?.username || ""}
                    onChange={(_, value) => setCreateForm((prev) => ({ ...prev, people: value || [] }))}
                    isOptionEqualToValue={(option, value) => option.username === value.username}
                    renderInput={(params) => <TextField {...params} fullWidth label="Project Person" />}
                  />
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700 }}>
                Project Information
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sx={{ display: "flex", flexGrow: 1 }}>
                  <TextField
                    fullWidth
                    select
                    sx={{ ...addProjectFieldSx, flexGrow: 1 }}
                    label="Project Status"
                    value={createForm.status}
                    onChange={(event) => setCreateForm((prev) => ({ ...prev, status: event.target.value }))}
                  >
                    {["active", "completed", "submitted", "other"].map((value) => (
                      <MenuItem key={value} value={value}>
                        {toTitleCase(value)}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sx={{ display: "flex", flexGrow: 1 }}>
                  <TextField
                    fullWidth
                    select
                    sx={{ ...addProjectFieldSx, flexGrow: 1 }}
                    label="Project Type"
                    value={createForm.projectType}
                    onChange={(event) => setCreateForm((prev) => ({ ...prev, projectType: event.target.value }))}
                  >
                    {["internal", "external", "research", "other"].map((value) => (
                      <MenuItem key={value} value={value}>
                        {toTitleCase(value)}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sx={{ display: "flex", flexGrow: 1 }}>
                  <TextField
                    fullWidth
                    sx={{ ...addProjectFieldSx, flexGrow: 1 }}
                    type="number"
                    label="Number of Schedules"
                    value={createForm.numberOfSchedules}
                    onChange={(event) =>
                      setCreateForm((prev) => ({ ...prev, numberOfSchedules: Number(event.target.value || 1) }))
                    }
                    inputProps={{ min: 1, max: 20 }}
                    helperText="Minimum 1, maximum 20"
                  />
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700 }}>
                Project Dates
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sx={{ display: "flex", flexGrow: 1 }}>
                  <DatePicker
                    label="Start Date"
                    format="MM/DD/YYYY"
                    value={createForm.startDate ? dayjs(createForm.startDate) : null}
                    onChange={(value) =>
                      setCreateForm((prev) => ({
                        ...prev,
                        startDate: value && value.isValid() ? value.format("YYYY-MM-DD") : "",
                      }))
                    }
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        required: true,
                        sx: { ...addProjectFieldSx, flexGrow: 1 },
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} sx={{ display: "flex", flexGrow: 1 }}>
                  <DatePicker
                    label="End Date"
                    format="MM/DD/YYYY"
                    value={createForm.endDate ? dayjs(createForm.endDate) : null}
                    onChange={(value) =>
                      setCreateForm((prev) => ({
                        ...prev,
                        endDate: value && value.isValid() ? value.format("YYYY-MM-DD") : "",
                      }))
                    }
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        required: true,
                        sx: { ...addProjectFieldSx, flexGrow: 1 },
                      },
                    }}
                  />
                </Grid>
              </Grid>
            </Box>
          ) : (
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700 }}>
                Schedule Configuration
              </Typography>

              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Created Project ID"
                    value={createdProject?.pid || ""}
                    disabled
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
              </Grid>

              {(scheduleRows || []).map((row) => (
                  <Box
                    key={row.position}
                    sx={{
                      mb: 2,
                      p: 2,
                      border: "1px solid #d7dbe8",
                      borderRadius: 1,
                      bgcolor: row.locked ? "#f8f9fc" : "#ffffff",
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700 }}>
                      Schedule {row.position}
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <TextField fullWidth label="Serial Number" value={row.position} disabled />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          select
                          label="Type of Visit"
                          value={row.type}
                          onChange={(event) => handleScheduleChange(row.position, "type", event.target.value)}
                          disabled={row.locked || row.position === 1}
                        >
                          <MenuItem value="static">Static</MenuItem>
                          <MenuItem value="dynamic">Dynamic</MenuItem>
                        </TextField>
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          type="number"
                          label="Offset (days)"
                          value={row.type === "static" ? 0 : row.offset}
                          onChange={(event) => handleScheduleChange(row.position, "offset", event.target.value)}
                          disabled={row.locked || row.type === "static"}
                          inputProps={{ min: 1 }}
                          helperText={row.type === "static" ? "Static visit keeps offset as 0" : "Minimum 1"}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          type="number"
                          label="Tolerance Buffer (days)"
                          value={row.type === "static" ? 0 : row.tolerance}
                          onChange={(event) => handleScheduleChange(row.position, "tolerance", event.target.value)}
                          disabled={row.locked || row.type === "static"}
                          inputProps={{ min: 0 }}
                          helperText={row.type === "static" ? "Static visit keeps tolerance as 0" : "Minimum 0"}
                        />
                      </Grid>
                    </Grid>
                  </Box>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleAbortCreateFlow} disabled={creating || addingTimeline || abortingCreate}>
            {abortingCreate ? "Cancelling..." : "Cancel"}
          </Button>
          {activeStep === 0 ? (
            <Button onClick={handleCreateProjectStep} variant="contained" disabled={creating || abortingCreate}>
              {creating ? "Creating..." : "Create Project"}
            </Button>
          ) : (
            <>
              <Button onClick={handleBackToCreateStep} disabled={addingTimeline || abortingCreate}>
                {abortingCreate ? "Going Back..." : "Back"}
              </Button>
              <Button onClick={handleAddSchedulesStep} variant="contained" disabled={addingTimeline || abortingCreate}>
                {addingTimeline ? "Submitting..." : "Add Schedules"}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      <AppToast
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
      />

      <Tooltip
        title={
          <Typography sx={{ fontSize: "1rem", fontWeight: "bold" }}>
            Add Project
          </Typography>
        }
      >
        <Fab
          color="primary"
          aria-label="add-project"
          onClick={() => {
            if (!canCreateProject) {
              setSnackbar({
                open: true,
                message: "Only admin or manager can create projects",
                severity: "warning",
              });
              return;
            }
            setCreateOpen(true);
          }}
          sx={{
            position: "fixed",
            bottom: 32,
            right: 32,
            zIndex: 1000,
            bgcolor: "#2a366e",
            "&:hover": {
              backgroundColor: "#424d80",
            },
          }}
        >
          <AddIcon />
        </Fab>
      </Tooltip>
    </Box>
  );
};

export default ProjectList;
